import React, { useEffect, useState } from "react";
import {
  useFetcher,
  Link,
  useSearchParams,
  useNavigate,
} from "@remix-run/react";
import { loader as searchLoader } from "~/routes/_app.search/route";
import type { FBProfile } from "~/.server/api";

import { Card } from "~/components/card";
import SearchBar from "~/routes/_app.search/components/search-bar";

interface SearchModalProps {
  onClose?: () => void; // Add a prop for closing the modal
}

export default function SearchModal({ onClose }: SearchModalProps) {
  const [searchParams] = useSearchParams();
  const fetcher = useFetcher<typeof searchLoader>();
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState<FBProfile[]>([]);
  const [recentlyVisitedProfiles, setRecentlyVisitedProfiles] = useState<
    FBProfile[]
  >([]);
  const [lastSearchedProfiles, setLastSearchedProfiles] = useState<FBProfile[]>(
    []
  );

  // On mount & whenever ?q changes, re-fetch
  useEffect(() => {
    const value = searchParams.get("q") ?? "";
    fetcher.submit(
      { q: value },
      {
        method: "GET",
        action: "/search?",
      }
    );
  }, [searchParams]);

  // Update local states when data arrives
  useEffect(() => {
    if (fetcher.data) {
      setProfiles(fetcher.data.profiles || []);
      setRecentlyVisitedProfiles(fetcher.data.recentlyVisitedProfiles || []);
      setLastSearchedProfiles(fetcher.data.lastSearchedProfiles || []);
    }
  }, [fetcher.data]);

  // Handler to close modal and navigate
  const handleProfileClick = (profileId: string) => {
    if (onClose) {
      onClose(); // Close the modal first
    }
    navigate(`/user-profile/${profileId}`); // Then navigate
  };

  return (
    // Outer container: fixed height, so it doesn't exceed the viewport
    <div className="flex flex-col h-[80vh] w-full md:w-[80vw] md:max-w-[50vw] overflow-hidden text-primary rounded-2xl shadow-2xl bg-background/95 backdrop-blur-xl border border-white/10">
      {/* Top section (heading + search bar) pinned */}
      <div className="p-4 flex-shrink-0">
        <h2 className="text-2xl font-semibold text-center mb-4 text-text">
          Search Profiles
        </h2>

        <div className="mb-0">
          <SearchBar profiles={profiles} />
        </div>
      </div>

      {/* Scrollable area for results */}
      <div className="flex-1 overflow-y-auto pb-4 mx-4">
        {/* If there are profiles, show them */}
        {profiles.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-bold text-text2 uppercase tracking-wider mb-3 px-1">Search Results</h3>
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {profiles.map((profile) => (
                <li
                  key={profile.id}
                  className="bg-white/5 hover:bg-white/10 border border-white/5 hover:border-primary/30 rounded-2xl transition-all cursor-pointer overflow-hidden group"
                  onClick={() => handleProfileClick(profile.id)}
                >
                  <div className="p-3 flex flex-col items-center gap-3">
                    <img
                      className="w-16 h-16 rounded-full object-cover border-2 border-transparent group-hover:border-primary transition-colors"
                      src={profile.avatar_url || "/default-profile.jpg"}
                      alt={profile.display_name}
                    />
                    <div className="text-center w-full">
                      <span className="text-sm font-bold text-text block truncate px-2">
                        {profile.display_name}
                      </span>
                      <span className="text-xs text-text2 block mt-0.5">
                        View Profile
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recently Viewed Section */}
        {recentlyVisitedProfiles.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-bold text-text2 uppercase tracking-wider mb-3 px-1">Recently Viewed</h3>
            <ul className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {recentlyVisitedProfiles.map((profile) => (
                <li
                  key={profile.id}
                  className="bg-white/5 hover:bg-white/10 border border-white/5 hover:border-primary text-center rounded-xl p-2 transition-all cursor-pointer group"
                  onClick={() => handleProfileClick(profile.id)}
                >
                  <img
                    className="w-10 h-10 rounded-full object-cover mx-auto mb-2 opacity-80 group-hover:opacity-100 transition-opacity"
                    src={profile.avatar_url || "/default-profile.jpg"}
                    alt={profile.display_name}
                  />
                  <span className="text-xs text-text truncate block w-full">
                    {profile.display_name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Empty State */}
        {recentlyVisitedProfiles.length === 0 &&
          profiles.length === 0 &&
          lastSearchedProfiles.length === 0 && (
            <Card className="mt-4">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <img
                  src="/flirtybumblack.svg"
                  alt="No Profiles"
                  className="w-24 h-24 opacity-50 mb-4"
                />
                <p className="text-sm text-gray-500">
                  No profiles to display. Start searching to find profiles!
                </p>
              </div>
            </Card>
          )}
      </div>
    </div>
  );
}
